import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Lock } from "lucide-react";
import { mediaUrl } from "../config";

// Player that hard-stops a locked paid series at the preview limit.
// The video element has NO controls and cannot seek past previewSec; when the
// preview ends it pauses, blanks the frame and shows the paywall. The locked part
// cannot be reached by skipping — currentTime is clamped on timeupdate/seeking.
const GatedPlayer = ({ video, previewSec, locked, className = "", onLocked }) => {
  const ref = useRef(null);
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !video) return;
    setPreparing(false);
    const url = video.webVideoUrl || video.videoUrl;
    if (!url) return;
    const src = mediaUrl(url);
    const isHls = video.streamType === "hls" || src.includes(".m3u8");
    let hls;
    if (isHls) {
      if (el.canPlayType("application/vnd.apple.mpegurl")) el.src = src;
      else if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(src);
        hls.attachMedia(el);
      } else setPreparing(true);
    } else {
      el.src = src;
    }
    return () => { if (hls) hls.destroy(); };
  }, [video]);

  // Enforce the preview limit on the video element itself.
  useEffect(() => {
    const el = ref.current;
    if (!el || !locked || !previewSec) return;
    const clamp = () => {
      if (el.currentTime > previewSec) {
        el.pause();
        el.currentTime = previewSec;
        onLocked && onLocked();
      }
    };
    const onSeek = () => {
      if (el.currentTime > previewSec) el.currentTime = previewSec;
    };
    el.addEventListener("timeupdate", clamp);
    el.addEventListener("seeking", onSeek);
    el.addEventListener("seeked", onSeek);
    return () => {
      el.removeEventListener("timeupdate", clamp);
      el.removeEventListener("seeking", onSeek);
      el.removeEventListener("seeked", onSeek);
    };
  }, [locked, previewSec, onLocked]);

  if (preparing) {
    return (
      <div className={`relative flex items-center justify-center overflow-hidden bg-surface ${className}`} data-testid="player-preparing">
        {mediaUrl(video?.thumbnail) && <img src={mediaUrl(video.thumbnail)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />}
        <p className="relative font-heading text-sm font-semibold text-white">Getting ready…</p>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      controls={!locked}
      controlsList={locked ? "nodownload noplaybackrate nofullscreen" : undefined}
      disablePictureInPicture={locked}
      playsInline
      autoPlay
      poster={mediaUrl(video?.thumbnail) || undefined}
      className={`bg-black ${className}`}
      data-testid="video-player"
    />
  );
};

export const LockedOverlay = ({ title }) => (
  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/85 text-center" data-testid="locked-overlay">
    <Lock size={28} className="text-crimson" />
    <p className="mt-3 font-heading text-base font-bold text-white">Preview ended</p>
    <p className="mt-1 text-sm text-slate-400">Pay to keep watching {title ? `“${title}”` : "this series"}.</p>
  </div>
);

export default GatedPlayer;
